/**
 * @file pages/Outbound.jsx
 * @description Outbound travel page. Displays international destinations
 * organized by continent with country selection and tour navigation.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Components/Layouts/Header';
import NikabaFooter from '../Components/Layouts/Footer';

const countries = [
  {
    code: "TH",
    name: "Thailand",
    continent: "Asia",
    featured: true
  },
  {
    code: "SG",
    name: "Singapore",
    continent: "Asia",
    featured: true
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    continent: "Asia",
    featured: true
  },
  {
    code: "MY",
    name: "Malaysia",
    continent: "Asia"
  },
  {
    code: "ID",
    name: "Indonesia",
    continent: "Asia",
    featured: true
  },
  {
    code: "US",
    name: "United States",
    continent: "North America",
    featured: true
  },
  {
    code: "CA",
    name: "Canada",
    continent: "North America"
  },
  {
    code: "GB",
    name: "United Kingdom",
    continent: "Europe",
    featured: true
  },
  {
    code: "AU",
    name: "Australia",
    continent: "Oceania",
    featured: true
  },
  {
    code: "FR",
    name: "France",
    continent: "Europe",
    featured: true
  },
  {
    code: "CH",
    name: "Switzerland",
    continent: "Europe",
    featured: true
  },
  {
    code: "MV",
    name: "Maldives",
    continent: "Asia",
    featured: true
  },
  {
    code: "LK",
    name: "Sri Lanka",
    continent: "Asia"
  },
  {
    code: "NP",
    name: "Nepal",
    continent: "Asia"
  },
  {
    code: "BT",
    name: "Bhutan",
    continent: "Asia"
  },
  {
    code: "DE",
    name: "Germany",
    continent: "Europe"
  },
  {
    code: "NZ",
    name: "New Zealand",
    continent: "Oceania"
  },
  {
    code: "IT",
    name: "Italy",
    continent: "Europe",
    featured: true
  },
  {
    code: "ES",
    name: "Spain",
    continent: "Europe"
  },
  {
    code: "GR",
    name: "Greece",
    continent: "Europe",
    featured: true
  },
  {
    code: "TR",
    name: "Turkey",
    continent: "Europe"
  },
  {
    code: "VN",
    name: "Vietnam",
    continent: "Asia"
  },
  {
    code: "JP",
    name: "Japan",
    continent: "Asia",
    featured: true
  },
  {
    code: "KR",
    name: "South Korea",
    continent: "Asia"
  },
  {
    code: "EG",
    name: "Egypt",
    continent: "Africa",
    featured: true
  },
  {
    code: "RU",
    name: "Russia",
    continent: "Europe"
  },
  {
    code: "NL",
    name: "Netherlands",
    continent: "Europe"
  },
  {
    code: "BE",
    name: "Belgium",
    continent: "Europe"
  },
  {
    code: "AT",
    name: "Austria",
    continent: "Europe"
  },
  {
    code: "CZ",
    name: "Czech Republic",
    continent: "Europe"
  },
  {
    code: "PT",
    name: "Portugal",
    continent: "Europe"
  },
  {
    code: "IE",
    name: "Ireland",
    continent: "Europe"
  },
  {
    code: "FI",
    name: "Finland",
    continent: "Europe"
  },
  {
    code: "SE",
    name: "Sweden",
    continent: "Europe"
  },
  {
    code: "NO",
    name: "Norway",
    continent: "Europe"
  },
  {
    code: "DK",
    name: "Denmark",
    continent: "Europe"
  },
  {
    code: "KH",
    name: "Cambodia",
    continent: "Asia"
  },
  {
    code: "LA",
    name: "Laos",
    continent: "Asia"
  },
  {
    code: "PH",
    name: "Philippines",
    continent: "Asia"
  },
  {
    code: "MU",
    name: "Mauritius",
    continent: "Africa"
  },
  {
    code: "SC",
    name: "Seychelles",
    continent: "Africa"
  },
  {
    code: "FJ",
    name: "Fiji",
    continent: "Oceania"
  },
  {
    code: "ZA",
    name: "South Africa",
    continent: "Africa"
  },
  {
    code: "KE",
    name: "Kenya",
    continent: "Africa"
  },
  {
    code: "TZ",
    name: "Tanzania",
    continent: "Africa"
  },
  {
    code: "MA",
    name: "Morocco",
    continent: "Africa"
  },
  {
    code: "IS",
    name: "Iceland",
    continent: "Europe"
  },
  {
    code: "HR",
    name: "Croatia",
    continent: "Europe"
  },
  {
    code: "HU",
    name: "Hungary",
    continent: "Europe"
  },
  {
    code: "PL",
    name: "Poland",
    continent: "Europe"
  }
];

const Outbound = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('All');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const continents = ['All', 'Asia', 'Europe', 'North America', 'South America', 'Africa', 'Oceania'];

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesContinent = selectedContinent === 'All' || country.continent === selectedContinent;
    const matchesFeatured = !showFeaturedOnly || country.featured;
    return matchesSearch && matchesContinent && matchesFeatured;
  });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#FDFDFF] to-[#F0F0F5] pt-24">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 text-[#243185]">Explore the World</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover amazing destinations, immerse yourself in different cultures, and create unforgettable memories.
          </p>
        </div>
        
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search countries..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5365dc] focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <select
              className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5365dc] focus:border-transparent"
              value={selectedContinent}
              onChange={(e) => setSelectedContinent(e.target.value)}
            >
              {continents.map(continent => (
                <option key={continent} value={continent}>
                  {continent}
                </option>
              ))}
            </select>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showFeaturedOnly}
                onChange={(e) => setShowFeaturedOnly(e.target.checked)}
                className="form-checkbox h-5 w-5 text-[#5365dc]"
              />
              <span className="text-gray-700">Featured Destinations</span>
            </label>
          </div>
        </div>

        {/* Countries Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredCountries.map(country => (
            <div
              key={country.code}
              className="group bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-1 transition-all duration-300"
            >
              <div className="relative aspect-w-3 aspect-h-2 bg-gradient-to-b from-[#5365dc] to-[#243185]">
                <img
                  src={`https://flagcdn.com/w320/${country.code.toLowerCase()}.png`}
                  alt={`${country.name} flag`}
                  className="w-full h-[80px] object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
                {country.featured && (
                  <div className="absolute top-2 right-2 bg-yellow-400 text-xs font-bold px-2 py-1 rounded-full">
                    Featured
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-xl font-semibold mb-2 text-[#243185]">{country.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{country.continent}</p>
                <button
                  onClick={() => navigate(`/travel-guide/${country.code.toLowerCase()}`)}
                  className="w-full bg-gradient-to-r from-[#5365dc] to-[#243185] text-white px-6 py-2 rounded-full hover:from-[#243185] hover:to-[#5365dc] transition-all duration-300 transform hover:scale-105"
                >
                  View Guide
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      <NikabaFooter />
    </div>
  );
};

export default Outbound; 